import { useAuth0 } from '../../../auth/AuthProvider';
import { Chip, Divider } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import useGetDiveEquipment from '../../../hooks/useGetDiveEquipment';
import useUpsertDiveEquipment from '../../../hooks/useUpsertDiveEquipment';
import { displayEmpty } from '../../../common/utilities/displayEmpty';
import ProfileSubpageState from '../components/ProfileSubpageState';

type EquipmentState = {
  BCD: string;
  Wetsuit: string;
  Fins: string;
};

const emptyEquipmentState: EquipmentState = {
  BCD: '',
  Wetsuit: '',
  Fins: '',
};

const DiveEquipment = () => {
  const { user } = useAuth0();
  const { diveEquipment, isLoading, isError, refetch, isFetching } =
    useGetDiveEquipment(user?.sub ?? '', true);
  const upsertDiveEquipment = useUpsertDiveEquipment();

  const [editMode, setEditMode] = useState(false);
  const [equipmentState, setEquipmentState] =
    useState<EquipmentState>(emptyEquipmentState);

  const syncFromServer = () => {
    setEquipmentState({
      BCD: diveEquipment?.bcd || '',
      Wetsuit: diveEquipment?.wetsuit || '',
      Fins: diveEquipment?.fins || '',
    });
  };

  useEffect(() => {
    if (!editMode) {
      syncFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diveEquipment, editMode]);

  const handleSelect = (type: keyof EquipmentState, size: string) => {
    setEquipmentState(prev => ({ ...prev, [type]: size }));
  };

  const handleCancel = () => {
    syncFromServer();
    setEditMode(false);
  };

  const handleSaveChanges = async (event: FormEvent) => {
    event.preventDefault();
    if (!user?.sub) {
      return;
    }

    try {
      await upsertDiveEquipment.mutateAsync({
        equipmentId: diveEquipment?.equipmentId || -1,
        userId: user.sub,
        bcd: equipmentState.BCD,
        wetsuit: equipmentState.Wetsuit,
        fins: equipmentState.Fins,
      });
      setEditMode(false);
    } catch {
      // Keep edit mode open; toast handled by mutation feedback.
    }
  };

  const sizeOptions: Record<keyof EquipmentState, string[]> = {
    BCD: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    Wetsuit: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    Fins: ['34-35', '36-37', '38-39', '40-41', '42-43', '44-45'],
  };

  if (isLoading || isError) {
    return (
      <ProfileSubpageState
        title="Dive equipment"
        isLoading={isLoading}
        isError={isError}
        errorMessage="We couldn’t load your dive equipment sizes. Please try again."
        onRetry={() => {
          void refetch();
        }}
        isRetrying={isFetching && isError}
      />
    );
  }

  const savedEquipmentState: EquipmentState = {
    BCD: diveEquipment?.bcd || '',
    Wetsuit: diveEquipment?.wetsuit || '',
    Fins: diveEquipment?.fins || '',
  };
  const isEquipmentChanged =
    savedEquipmentState.BCD !== equipmentState.BCD ||
    savedEquipmentState.Wetsuit !== equipmentState.Wetsuit ||
    savedEquipmentState.Fins !== equipmentState.Fins;
  const isSaveDisabled = upsertDiveEquipment.isPending || !isEquipmentChanged;

  return (
    <div className="px-6 md:px-0 md:min-w-[772px]">
      <div className="flex flex-col">
        <span className="text-2xl md:text-3xl font-semibold text-neutral-800">
          Dive equipment
        </span>
        <span className="mt-2 text-base text-neutral-500">
          Please specify the diving rental gear you require. We will use this
          information to inform the boat if you choose to rent equipment on your
          trip.
        </span>
      </div>
      <form className="mt-6" onSubmit={handleSaveChanges}>
        <div className="flex justify-between font-semibold">
          <span className="text-xl">Dive equipment</span>
          <button
            type="button"
            className="underline"
            onClick={() => (editMode ? handleCancel() : setEditMode(true))}
          >
            {editMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {(Object.keys(sizeOptions) as Array<keyof EquipmentState>).map(
          (equipment, idx) => (
            <div key={equipment} className="flex flex-col mt-4">
              <span className="text-neutral-500">{equipment}</span>
              {editMode ? (
                <div className="mt-4 mb-4">
                  <span className="text-sm text-neutral-800">Size</span>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {sizeOptions[equipment].map(size => (
                      <Chip
                        key={size}
                        label={size}
                        variant={
                          equipmentState[equipment] === size
                            ? 'filled'
                            : 'outlined'
                        }
                        onClick={() => handleSelect(equipment, size)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <span className="mt-2 mb-4 text-neutral-800">
                  Size: {displayEmpty(equipmentState[equipment])}
                </span>
              )}
              {idx < 2 && <Divider />}
            </div>
          ),
        )}
        {editMode && (
          <button
            type="submit"
            className="mt-4 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSaveDisabled}
          >
            {upsertDiveEquipment.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
    </div>
  );
};

export default DiveEquipment;
